import {
  createPropertyFromValuationFunnel,
  getMatchingPropertyIdForFunnelAbandonment,
} from '@/api/propertyValuation';
import { useAuth0Client } from '@/composables/useAuth0Client';
import {
  buildValuationFunnelAbandonmentPayload,
  getFunnelAbandonmentAddress,
  getFunnelAbandonmentFormattedAddress,
  navigateToHomeOwnerWorldPropertyDetails,
  PendingNavigation,
  shouldShowFunnelAbandonmentPrompt,
} from '@/composables/useFunnelAbandonmentGuard.helpers';
import { isNavigableAnchor } from '@/helpers/Browser/browser';
import {
  isQueryParamFeatureEnabled,
  QueryParamFeatures,
} from '@/helpers/FeatureToggle/queryParamFeatureHelper';
import { isExternalFunnelNavigation, isLeavingValuationFunnel } from '@/helpers/Route/routeHelper';
import { storeFunnelAbandonmentPropertyPayload } from '@/helpers/SessionStorage/funnelAbandonmentSessionStorage';
import { usePropertyDetailsStore } from '@/stores/propertyDetails/propertyDetails';
import { usePropertyValuationStore } from '@/stores/propertyValuation';
import { HomeOwnerWorldPropertyFromValuationFunnelRequest } from '@/stores/types/propertyValuation';
import { trackingClickEvent } from '@/tracking/tracking';
import { TRACKING_ACTION, TRACKING_LABEL } from '@/tracking/types';
import { LoginAction, RedirectAppState, ToastType } from '@/types';
import { Auth0VueClient } from '@auth0/auth0-vue';
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { storeToRefs } from 'pinia';
import { computed, onBeforeUnmount, onMounted, Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { onBeforeRouteLeave, Router, useRoute, useRouter } from 'vue-router';

interface AbandonmentGuardRefs {
  isModalVisible: Ref<boolean>;
  isAuthModalVisible: Ref<boolean>;
  isCreatingProperty: Ref<boolean>;
  isAbandonmentPromptRegistered: Ref<boolean>;
  pendingNavigation: Ref<PendingNavigation | null>;
  pendingAuthPayload: Ref<HomeOwnerWorldPropertyFromValuationFunnelRequest | null>;
}

const createAbandonmentHandlers = ({
  propertyValuationStore,
  propertyDetailsStore,
  router,
  locale,
  auth0Client,
  refs,
  canInterceptAbandonment,
  funnelAddress,
}: {
  propertyValuationStore: ReturnType<typeof usePropertyValuationStore>;
  propertyDetailsStore: ReturnType<typeof usePropertyDetailsStore>;
  router: Router;
  locale: Ref<string>;
  auth0Client: Auth0VueClient | null;
  refs: AbandonmentGuardRefs;
  canInterceptAbandonment: Ref<boolean>;
  funnelAddress: Ref<ReturnType<typeof getFunnelAbandonmentAddress>>;
}) => {
  const buildPayload = () =>
    buildValuationFunnelAbandonmentPayload(
      propertyValuationStore,
      locale.value as HgSupportedLanguage,
    );

  const isUserAuthenticated = () => Boolean(auth0Client?.isAuthenticated.value);

  const openAbandonmentModal = (navigation: PendingNavigation): void => {
    refs.isAbandonmentPromptRegistered.value = false;
    refs.pendingNavigation.value = navigation;
    refs.isModalVisible.value = true;
  };

  const tryInterceptNavigation = async (
    navigation: PendingNavigation,
  ): Promise<boolean | undefined> => {
    if (!canInterceptAbandonment.value) {
      return undefined;
    }

    if (isUserAuthenticated()) {
      const propertyId = await getMatchingPropertyIdForFunnelAbandonment(funnelAddress.value);
      if (propertyId) {
        return undefined;
      }
    }

    openAbandonmentModal(navigation);
    return false;
  };

  const confirmLeave = async (): Promise<void> => {
    const leadId = propertyValuationStore.data.leadId;

    trackingClickEvent(
      TRACKING_ACTION.VIEW_PROPERTY_DASHBOARD,
      TRACKING_LABEL.VALUATION_FUNNEL_AFTER_ADDRESS_ENTRY,
      true,
      leadId ? { h_lead_id_066: leadId } : undefined,
    );

    if (isUserAuthenticated()) {
      refs.isCreatingProperty.value = true;
      const response = await createPropertyFromValuationFunnel(buildPayload());
      refs.isCreatingProperty.value = false;

      if (!response.propertyId) {
        propertyDetailsStore.setToastType(ToastType.AddFailure);
        return;
      }

      refs.isModalVisible.value = false;
      refs.pendingNavigation.value = null;

      navigateToHomeOwnerWorldPropertyDetails(
        propertyDetailsStore,
        router,
        locale.value as HgSupportedLanguage,
        response.propertyId,
      );
      return;
    }

    refs.pendingAuthPayload.value = buildPayload();
    refs.isModalVisible.value = false;
    refs.isAuthModalVisible.value = true;
  };

  const cancelLeave = (): void => {
    refs.isModalVisible.value = false;
    refs.pendingNavigation.value = null;
  };

  const closeAuthModal = (): void => {
    refs.isAuthModalVisible.value = false;
    refs.pendingAuthPayload.value = null;
  };

  const confirmAuthModal = async (): Promise<void> => {
    const payload = refs.pendingAuthPayload.value ?? buildPayload();
    refs.isAuthModalVisible.value = false;
    refs.pendingAuthPayload.value = null;
    refs.pendingNavigation.value = null;

    const appState: RedirectAppState = {
      action: LoginAction.CreatePropertyFromValuationFunnel,
      payload,
      locale: locale.value as HgSupportedLanguage,
    };

    storeFunnelAbandonmentPropertyPayload(payload);
    await (auth0Client as Auth0VueClient).loginWithRedirect({ appState });
  };

  const handleDocumentClick = async (event: MouseEvent): Promise<void> => {
    if (
      refs.isModalVisible.value ||
      refs.isAuthModalVisible.value ||
      !canInterceptAbandonment.value ||
      !(event.target instanceof Element)
    ) {
      return;
    }

    const anchor = event.target.closest('a[href]');
    if (!(anchor instanceof HTMLAnchorElement) || !isNavigableAnchor(anchor)) {
      return;
    }

    const destinationUrl = new URL(anchor.href, window.location.origin);
    const currentUrl = new URL(window.location.href);

    if (!isExternalFunnelNavigation(destinationUrl, currentUrl)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const shouldBlock = await tryInterceptNavigation({ type: 'href', href: anchor.href });
    if (shouldBlock !== false) {
      window.location.assign(anchor.href);
    }
  };

  return {
    tryInterceptNavigation,
    handleDocumentClick,
    confirmLeave,
    cancelLeave,
    closeAuthModal,
    confirmAuthModal,
  };
};

export const useFunnelAbandonmentGuard = () => {
  const propertyValuationStore = usePropertyValuationStore();
  const propertyDetailsStore = usePropertyDetailsStore();
  const router = useRouter();
  const route = useRoute();
  const { locale } = useI18n();
  const auth0Client = useAuth0Client();
  const { data: formData, step: formStep } = storeToRefs(propertyValuationStore);

  const refs: AbandonmentGuardRefs = {
    isModalVisible: ref(false),
    isAuthModalVisible: ref(false),
    isCreatingProperty: ref(false),
    isAbandonmentPromptRegistered: ref(false),
    pendingNavigation: ref(null),
    pendingAuthPayload: ref(null),
  };

  const formattedAddress = computed(() =>
    getFunnelAbandonmentFormattedAddress(propertyValuationStore),
  );
  const funnelAddress = computed(() => getFunnelAbandonmentAddress(propertyValuationStore));

  const canInterceptAbandonment = computed(() =>
    shouldShowFunnelAbandonmentPrompt({
      routePath: route.path,
      formData: formData.value,
      formStep: formStep.value,
      isAbandonmentPromptRegistered: refs.isAbandonmentPromptRegistered.value,
      isAbandonmentModalFeatureEnabled: isQueryParamFeatureEnabled(
        QueryParamFeatures.ABANDONMENT_MODAL,
        route.query,
      ),
    }),
  );

  const handlers = createAbandonmentHandlers({
    propertyValuationStore,
    propertyDetailsStore,
    router,
    locale,
    auth0Client,
    refs,
    canInterceptAbandonment,
    funnelAddress,
  });

  onBeforeRouteLeave(async (to) => {
    if (refs.isModalVisible.value || refs.isAuthModalVisible.value) {
      return false;
    }

    if (!canInterceptAbandonment.value || !isLeavingValuationFunnel(to.path)) {
      return;
    }

    return handlers.tryInterceptNavigation({ type: 'route', to });
  });

  onMounted(() => {
    refs.isAbandonmentPromptRegistered.value = true;
    document.addEventListener('click', handlers.handleDocumentClick, true);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('click', handlers.handleDocumentClick, true);
  });

  return {
    isModalVisible: refs.isModalVisible,
    isAuthModalVisible: refs.isAuthModalVisible,
    isCreatingProperty: refs.isCreatingProperty,
    formattedAddress,
    confirmLeave: handlers.confirmLeave,
    cancelLeave: handlers.cancelLeave,
    closeAuthModal: handlers.closeAuthModal,
    confirmAuthModal: handlers.confirmAuthModal,
  };
};
